import { useThemeImage } from "../../hooks/useThemeImage";
import droplet from "../../assets/droplet.png";
import exampleImg from "../../assets/example.png";
import xhsQr from "../../assets/KreneQrcode/xhs.png";
import dyQr from "../../assets/KreneQrcode/dy.png";
import wechatQr from "../../assets/KreneQrcode/wechat.png";
import bilibiliQr from "../../assets/KreneQrcode/bilibili.png";
import {uploadFileToOSS} from "../../api/chat-upload";
import {
    createActivityApplication,
    updateActivityApplication,
    getActivityApplications,
    ActivityApplicationItem,
    ActivityApplicationsResponse,
} from "../../api/memberActivities";
import {generateDeviceId, getUserInfo} from "../../util/author";
import {getLocalStorage, setLocalStorage} from "../../util/storage";
import {ChangeEvent, useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useMediaQuery} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import {useMatomoTrackingPoint} from "../../hooks/useMatomoTrackingPoint";
import "./styles.scss";
import "./stylesB.scss";

type Platform = 'douyin' | 'xiaohongshu' | 'bilibili' | 'wechat';

interface UploadItem {
    file: File;
    preview: string;
    fileName: string;   // OSS 文件名，后续提交用
    url: string;
    uploading: boolean;
    error?: string;
}

interface PlatformInfo {
    key: Platform;
    name: string;
    handle: string;
    color: string;
    followUrl: string;
    qrImage: string;
}

const PLATFORMS: PlatformInfo[] = [
    {
        key: 'douyin',
        name: '抖音',
        handle: '@Krene 创作空间',
        color: 'var(--c-text-primary)',
        followUrl: 'https://www.douyin.com/user/MS4wLjABAAAASYb6YOt4ylWkCpCCv5aBZTJNpcmbLtbZkA1i8wdh5Yc?from_tab_name=main',
        qrImage: dyQr,
    },
    {
        key: 'xiaohongshu',
        name: '小红书',
        handle: '@Krene 创作空间',
        color: '#FF2442',
        followUrl: 'https://www.xiaohongshu.com/user/profile/6a6993b0000000001400c400',
        qrImage: xhsQr,
    },
    {
        key: 'bilibili',
        name: 'Bilibili',
        handle: '@Krene创作空间',
        color: '#00A1D6',
        followUrl: 'https://space.bilibili.com/3706924126177319',
        qrImage: bilibiliQr,
    },
    {
        key: 'wechat',
        name: '微信公众号',
        handle: '@Krene 创作空间',
        color: 'var(--c-success)',
        followUrl: 'https://mp.weixin.qq.com/s/p40zc0fpohd0U4bNYLZSpA',
        qrImage: wechatQr,
    },
];

const EMPTY_UPLOADS: Record<Platform, UploadItem | null> = {
    douyin: null,
    xiaohongshu: null,
    bilibili: null,
    wechat: null,
};

const getDeviceId = (): string => {
    let deviceId = getLocalStorage('deviceId');
    if (!deviceId) {
        deviceId = generateDeviceId();
        setLocalStorage('deviceId', deviceId);
    }
    return deviceId;
};

const getUploadUserId = (): string => {
    const user = getUserInfo();
    if (user?.uuid) return user.uuid;
    return getDeviceId();
};

const BUTTON_STATUS_TEXT: Record<string, string> = {
    pending: '审核中',
    approved: '已通过',
    claimed: '已领取',
};

const extractApplications = (res: ActivityApplicationsResponse | null): ActivityApplicationItem[] => {
    if (!res) return [];
    if (Array.isArray(res.data)) return res.data;
    if (res.data && Array.isArray(res.data.items)) return res.data.items;
    return [];
};

const getRules = (isMobile: boolean): string[] => [
    '关注 Krene 官方账号：点击右侧【去关注】按钮跳转对应平台，或扫描二维码关注官方账号；',
    isMobile
        ? '上传截图：分别截取四个平台关注成功页面截图，上传至下方的对应入口；'
        : '上传截图：分别截取四个平台关注成功页面截图，上传至右侧的对应入口；',
    '提交申请：全部截图上传完成后，点击【提交按钮】提交活动申请；',
];

const REWARD_NOTES: Array<[string, string]> = [
    ['审核时效', '工作人员将在 3 个工作日内完成材料核验；'],
    ['奖励发放', '审核通过后，将在消息中心发送通知，可在活动页手动领取奖励；'],
    ['未通过处理', '若材料审核不通过，可根据驳回原因补充材料并重新参与活动；'],
    ['参与限制', '每人仅可参与一次活动，若发现截图造假、重复使用他人截图等情况，平台有权收回奖励。'],
];

export default function NewActive() {
    const logo = useThemeImage('logo');
    const { trackFollowActivityCommit, trackUploadFollowScreenshot, trackFollowAccountClick } = useMatomoTrackingPoint();
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width:768px)');
    const rules = getRules(isMobile);
    const [uploads, setUploads] = useState<Record<Platform, UploadItem | null>>(EMPTY_UPLOADS);
    const [missing, setMissing] = useState<Platform[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [successOpen, setSuccessOpen] = useState(false);
    const [submitError, setSubmitError] = useState<string>('');
    const [application, setApplication] = useState<ActivityApplicationItem | null>(null);
    const [previewImage, setPreviewImage] = useState<string | null>(null);

    const uploadsRef = useRef(uploads);
    const fileInputRefs = useRef<Record<Platform, HTMLInputElement | null>>({
        douyin: null,
        xiaohongshu: null,
        bilibili: null,
        wechat: null,
    });

    useEffect(() => {
        uploadsRef.current = uploads;
    }, [uploads]);

    // 卸载时释放所有 object URL
    useEffect(() => {
        return () => {
            Object.values(uploadsRef.current).forEach((item) => {
                if (item) URL.revokeObjectURL(item.preview);
            });
        };
    }, []);

    // 进入页面时查询当前用户的活动申请列表；无记录则先创建一条空申请（content 为空），后续提交走更新
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const res = await getActivityApplications();
            if (cancelled) return;
            let first = extractApplications(res)[0] || null;
            if (!first) {
                const created = await createActivityApplication({
                    type: 'by_screenshot',
                    content: {},
                    deviceId: getDeviceId(),
                });
                if (cancelled) return;
                const newId = (created?.data as {id?: string | number} | undefined)?.id;
                if (created?.success !== false && newId != null) {
                    first = {id: newId, status: 'pending', type: 'by_screenshot', content: {}} as ActivityApplicationItem;
                }
            }
            if (!cancelled) setApplication(first);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // 已有申请类型为 by_id 时应去填 id 版页面（/active-b）；by_screenshot 或无记录则留在本页
    useEffect(() => {
        if (application?.type === 'by_id') {
            navigate('/active-b');
        }
    }, [application, navigate]);

    const handleFileChange = (platform: Platform) => async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const platformName = PLATFORMS.find((p) => p.key === platform)?.name || platform;
        trackUploadFollowScreenshot({page_id: window.location.href, select_content: platformName});
        const preview = URL.createObjectURL(file);
        const old = uploadsRef.current[platform];
        if (old) URL.revokeObjectURL(old.preview);
        // 先进入上传中状态
        setUploads((prev) => ({
            ...prev,
            [platform]: {file, preview, fileName: '', url: '', uploading: true},
        }));
        // 清空 value，允许重复选择同一文件
        e.target.value = '';

        try {
            const result = await uploadFileToOSS(file, getUploadUserId());
            setUploads((prev) => ({
                ...prev,
                [platform]: {
                    file,
                    preview,
                    fileName: result.fileName,
                    url: result.url,
                    uploading: false,
                },
            }));
            setMissing((prev) => prev.filter((p) => p !== platform));
        } catch (err) {
            URL.revokeObjectURL(preview);
            setUploads((prev) => ({
                ...prev,
                [platform]: {file, preview, fileName: '', url: '', uploading: false, error: '图片上传失败，请重试'},
            }));
        }
    };

    const removeUpload = (platform: Platform) => {
        const old = uploadsRef.current[platform];
        if (old) URL.revokeObjectURL(old.preview);
        setUploads((prev) => ({...prev, [platform]: null}));
    };

    const triggerUpload = (platform: Platform) => {
        fileInputRefs.current[platform]?.click();
    };

    const isComplete = (platform: Platform) => {
        const item = uploads[platform];
        if (item && !item.uploading && !item.error && item.fileName) return true;
        // 驳回后已提交的截图也算完成，无需全部重新上传
        const existing = application?.content && typeof application.content[platform] === 'string'
            ? application.content[platform] as string
            : '';
        return Boolean(existing);
    };

    const allUploaded = PLATFORMS.every((p) => isComplete(p.key));

    // 已有实际提交内容且非「驳回」时才锁定提交按钮（自动创建的空记录不锁定）
    const hasSubmittedContent = Boolean(
        application &&
        application.content &&
        typeof application.content === 'object' &&
        Object.keys(application.content).length > 0
    );
    const isSubmitLocked = Boolean(hasSubmittedContent && application.status !== 'rejected');
    const submitButtonText = isSubmitLocked && application
        ? BUTTON_STATUS_TEXT[application.status] || application.status
        : application?.status === 'rejected'
        ? '重新提交'
        : '提交申请';
    const submitTooltip = application?.status === 'pending'
        ? '当前材料审核中，预计 3 个工作日内完成，请耐心等待审核结果'
        : application?.status === 'approved'
        ? '审核已通过，请在消息中心查看并领取奖励'
        : application?.status === 'claimed'
        ? '奖励已领取'
        : '';
    // 驳回状态下按钮始终高亮可点，不要求先补全材料；点击后由 handleSubmit 校验缺失项
    const submitEnabled = !isSubmitLocked && !submitting && (allUploaded || application?.status === 'rejected');

    const handleSubmit = async () => {
        const missingList = PLATFORMS.filter((p) => !isComplete(p.key)).map((p) => p.key);
        if (missingList.length > 0) {
            setMissing(missingList);
            return;
        }
        trackFollowActivityCommit({page_id: window.location.href});
        const content: Record<string, string> = {};
        PLATFORMS.forEach((p) => {
            const item = uploads[p.key];
            if (item?.fileName) {
                content[p.key] = item.fileName;
                return;
            }
            // 没重传的平台沿用已提交的截图
            const existing = application?.content && typeof application.content[p.key] === 'string'
                ? application.content[p.key] as string
                : '';
            if (existing) content[p.key] = existing;
        });

        setSubmitting(true);
        setSubmitError('');
        try {
            // 已有申请（id）则走更新接口，否则走创建接口
            const res = application
                ? await updateActivityApplication({
                    id: application.id,
                    type: 'by_screenshot',
                    content,
                })
                : await createActivityApplication({
                    type: 'by_screenshot',
                    content,
                    deviceId: getDeviceId(),
                });
            setSubmitting(false);
            if (res && res.success !== false) {
                setSuccessOpen(true);
                // 提交成功后刷新申请列表
                getActivityApplications().then((listRes) => {
                    setApplication(extractApplications(listRes)[0] || null);
                });
            } else {
                setSubmitError(res?.message || '提交失败，请重试');
            }
        } catch (err) {
            setSubmitting(false);
            setSubmitError('提交失败，请重试');
        }
    };

    const handleSuccessClose = () => {
        setSuccessOpen(false);
        setSubmitError('');
    };

    return (
        <div className="active-page">
            <div className="active-panel">
                <div className="active-header">
                    <img className="active-header-logo" src={logo} alt="Krene"/>
                    <div className="active-header-title"></div>
                </div>

                <div className="active-body">
                    <div className="active-left">
                    {/* 活动奖励 */}
                    <div className="active-reward">
                        <img className="active-reward-icon" src={droplet} alt=""/>
                        <div className="active-reward-text">
                            <div className="active-reward-title">🎉 Krene 全平台官号正式启动！</div>
                            <div className="active-reward-amount">
                                关注各平台官号，即可领取
                                <span className="active-reward-strong"> 50 </span>
                                水滴
                            </div>
                        </div>
                    </div>

                    <div className="active-intro">
                        官号将长期分享 AI 生图技巧、精品提示词、专属福利等，欢迎持续关注～
                    </div>

                    {/* 参与方式 */}
                    <div className="active-card">
                        <div className="active-card-title">参与方式</div>
                        <ul className="active-card-list">
                            {rules.map((rule, index) => (
                                <li key={index} data-index={index + 1}>{rule}</li>
                            ))}
                        </ul>
                    </div>

                    {/* 示例截图 */}
                    <div className="active-card">
                        <div className="active-card-title">示例截图</div>
                        <div className="active-card-desc">
                            以下为「关注成功」页面截图示例，请按照相同样式分别截图并上传：
                        </div>
                        <div className="active-example">
                            <img
                                className="active-example-img"
                                src={exampleImg}
                                alt="关注成功截图示例"
                                style={{cursor: 'pointer'}}
                                onClick={() => setPreviewImage(exampleImg)}
                            />
                        </div>
                    </div>

                    {/* 奖励说明 */}
                    <div className="active-card">
                        <div className="active-card-title">奖励说明</div>
                        <ul className="active-card-list">
                            {REWARD_NOTES.map(([title, content], index) => (
                                <li key={index} data-index={index + 1}>
                                    <span>{title}：</span>{content}
                                </li>
                            ))}
                        </ul>
                    </div>
                    </div>

                    <div className="active-right">
                    {application?.status === 'rejected' && (
                        <div className="active-rejected">
                            <div className="active-rejected-title">⚠️ 您的申请未通过审核</div>
                            {application.comment && (
                                <div className="active-rejected-reason">驳回原因：{application.comment}</div>
                            )}
                            <div className="active-rejected-tip">请补充关注截图后重新提交</div>
                        </div>
                    )}
                    <div className="active-upload-scroll">
                    {/* 截图上传 */}
                    {PLATFORMS.map((p) => {
                        const item = uploads[p.key];
                        const isMissing = missing.includes(p.key);
                        const existingUrl = application?.content && typeof application.content[p.key] === 'string'
                            ? application.content[p.key] as string
                            : '';
                        return (
                            <div
                                key={p.key}
                                className={`active-b-card${isMissing ? ' active-b-card-missing' : ''}`}
                            >
                                <div className="active-b-head">
                                    <div className="active-b-head-left">
                                        <span
                                            className="active-b-badge"
                                            style={{backgroundColor: p.color}}
                                        >
                                            {p.name}
                                        </span>
                                        <span className="active-b-scan-hint">{isMobile ? '扫码关注' : '可扫码关注'}</span>
                                    </div>
                                    <div className="active-b-head-right">
                                        <span className="active-b-handle">{p.handle}</span>
                                        <a
                                            className="active-b-follow"
                                            href={p.followUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            onClick={() => trackFollowAccountClick({page_id: window.location.href, select_content: p.name})}
                                        >
                                            去关注
                                        </a>
                                    </div>
                                </div>

                                <div className="active-b-main">
                                    <div className="active-b-qr">
                                        <img
                                            src={p.qrImage}
                                            alt={`${p.name}二维码`}
                                            style={{cursor: 'pointer'}}
                                            onClick={() => setPreviewImage(p.qrImage)}
                                        />
                                    </div>

                                    <div className="active-b-body">
                                        {item ? (
                                            item.uploading ? (
                                                <div className="active-upload-empty">
                                                    <span className="active-upload-plus">…</span>
                                                    <span>上传中...</span>
                                                </div>
                                            ) : item.error ? (
                                                <div
                                                    className="active-upload-empty"
                                                    onClick={() => triggerUpload(p.key)}
                                                >
                                                    <span className="active-upload-plus">!</span>
                                                    <span>上传失败，点击重试</span>
                                                </div>
                                            ) : (
                                                <div className="active-upload-preview">
                                                    <img
                                                        src={item.preview}
                                                        alt={p.name}
                                                        style={{cursor: 'pointer'}}
                                                        onClick={() => setPreviewImage(item.preview)}
                                                    />
                                                    <div className="active-upload-actions">
                                                        {isSubmitLocked ? (
                                                            <span>已提交</span>
                                                        ) : (
                                                            <>
                                                                <span onClick={() => triggerUpload(p.key)}>替换</span>
                                                                <span onClick={() => removeUpload(p.key)}>删除</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        ) : existingUrl ? (
                                            <div className="active-upload-preview">
                                                <img
                                                    src={existingUrl}
                                                    alt={p.name}
                                                    style={{cursor: 'pointer'}}
                                                    onClick={() => setPreviewImage(existingUrl)}
                                                />
                                                <div className="active-upload-actions">
                                                    {isSubmitLocked ? (
                                                        <span>已提交</span>
                                                    ) : (
                                                        <span onClick={() => triggerUpload(p.key)}>重新上传</span>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div
                                                className="active-upload-empty"
                                                onClick={() => triggerUpload(p.key)}
                                            >
                                                <span className="active-upload-plus">+</span>
                                                <span>上传{p.name}关注截图</span>
                                            </div>
                                        )}
                                        {isMissing && (
                                            <div className="active-upload-error">
                                                请上传{p.name}关注成功截图
                                            </div>
                                        )}
                                        <input
                                            ref={(el) => {
                                                fileInputRefs.current[p.key] = el;
                                            }}
                                            type="file"
                                            accept="image/*"
                                            hidden
                                            onChange={handleFileChange(p.key)}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    </div>

                    <div className="active-submit-bar">
                    {/* 提交 */}
                    <Tooltip
                        title={submitTooltip || ''}
                        arrow
                        placement="top"
                    >
                        <div
                            className="active-submit-btn"
                            style={{
                                backgroundColor: submitEnabled ? 'var(--c-brand)' : 'var(--c-border)',
                                cursor: submitEnabled ? 'pointer' : 'not-allowed',
                            }}
                            onClick={() => {
                                if (submitEnabled) handleSubmit();
                            }}
                        >
                            {submitting ? '提交中...' : submitButtonText}
                        </div>
                    </Tooltip>
                    <div className="active-submit-hint">
                        {isSubmitLocked
                            ? ''
                            : allUploaded
                            ? '提交后将在 3 个工作日内完成材料核验'
                            : '请先上传 4 个平台的关注成功截图'}
                    </div>
                    {submitError && <div className="active-submit-error">{submitError}</div>}
                    </div>
                    </div>
                </div>
            </div>

            {/* 成功弹窗 */}
            {successOpen && (
                <div className="active-mask" onClick={handleSuccessClose}>
                    <div className="active-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="active-dialog-icon">✓</div>
                        <div className="active-dialog-title">活动申请已提交</div>
                        <div className="active-dialog-content">
                            <div>感谢对 Krene 的关注和支持！</div>
                            <div>我们将在 3 个工作日内完成审核，并通过消息中心告知结果。</div>
                            <div>审核通过，即可在活动页领取奖励~</div>
                        </div>
                        <div className="active-dialog-actions">
                            <button className="active-dialog-btn" onClick={handleSuccessClose}>
                                知道了
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 图片预览 Dialog */}
            <Dialog
                open={!!previewImage}
                onClose={() => setPreviewImage(null)}
                PaperProps={{
                    sx: {maxWidth: 'none', margin: 0, backgroundColor: 'transparent', boxShadow: 'none', overflow: 'hidden'},
                }}
            >
                {previewImage && (
                    <img
                        src={previewImage}
                        alt="预览"
                        style={{
                            maxWidth: '92vw',
                            maxHeight: '92vh',
                            objectFit: 'contain',
                            display: 'block',
                            cursor: 'pointer',
                        }}
                        onClick={() => setPreviewImage(null)}
                    />
                )}
            </Dialog>
        </div>
    );
}
